import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "../../api";
import { SectionTitle, StatusPill, EmptyState, Avatar } from "../../components/Atoms";
import { AIAuditButton, useAIAudit } from "../../components/AIAuditPanel";
import { InstagramInsightsButton, useInstagramInsights } from "../../components/InstagramInsightsPanel";
import {
  ArrowLeft, MapPin, EnvelopeSimple, Phone, InstagramLogo, YoutubeLogo, LinkedinLogo,
  ChartLineUp, ClipboardText, CheckCircle, XCircle, Stack, GraduationCap,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminAmbassadorDetailPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [amb, setAmb] = useState(null);
  const [apps, setApps] = useState([]);
  const [subs, setSubs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openAudit, AuditModal } = useAIAudit();
  const { openInsights, InsightsModal } = useInstagramInsights();

  const load = async () => {
    try {
      const [a, app, sub, pay] = await Promise.all([
        api.get(`/ambassadors/${aid}`),
        api.get(`/applications`),
        api.get(`/submissions`),
        api.get(`/payouts`),
      ]);
      setAmb(a.data);
      setApps((app.data || []).filter(x => x.ambassador_id === aid));
      setSubs((sub.data || []).filter(x => x.ambassador_id === aid));
      setPayouts((pay.data || []).filter(x => x.ambassador_id === aid));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [aid]);

  const actApp = async (id, action) => {
    await api.post(`/applications/${id}/${action}`);
    toast.success(`Application ${action}d`);
    load();
  };

  if (loading) return <div className="skeleton h-64" />;
  if (!amb) return <EmptyState title="Ambassador not found" />;

  const totalEarned = payouts.filter(p => p.status === "Paid").reduce((a, p) => a + (p.amount || 0), 0);
  const skills = amb.skills || [];

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-from-amb">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex justify-end gap-2">
        <InstagramInsightsButton size="lg" onClick={openInsights} testIdSuffix="admin-detail" />
        <AIAuditButton ambassadorId={aid} size="lg" onClick={() => openAudit(aid)} testIdSuffix="admin-detail" />
      </div>

      {/* Header */}
      <div className="glass p-5">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <Avatar src={amb.photo} name={amb.name} size={72} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-white text-2xl font-bold truncate">{amb.name}</div>
              <StatusPill status={amb.status} />
            </div>
            <div className="text-sm text-muted flex items-center gap-3 flex-wrap mt-1">
              {amb.city && <span className="flex items-center gap-1.5"><MapPin size={14} /> {amb.city}</span>}
              {amb.college && <span className="flex items-center gap-1.5"><GraduationCap size={14} /> {amb.college}</span>}
              {amb.gender && <span data-testid="amb-gender" className="flex items-center gap-1.5">· {amb.gender}</span>}
              {amb.age && <span data-testid="amb-age">· {amb.age} yrs</span>}
            </div>
            <div className="text-sm text-muted flex items-center gap-3 flex-wrap mt-2">
              {amb.email && <span className="flex items-center gap-1.5" aria-label="Email"><EnvelopeSimple size={14} /> <span className="sr-only">Email:</span>{amb.email}</span>}
              {amb.phone && <span className="flex items-center gap-1.5" aria-label="Phone"><Phone size={14} /> <span className="sr-only">Phone:</span>{amb.phone}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {amb.instagram && <a href={`https://instagram.com/${(amb.instagram || "").replace("@","")}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#EC4899] flex items-center gap-1.5" data-testid="amb-instagram"><InstagramLogo size={14} weight="bold" /> {amb.instagram}</a>}
              {amb.youtube && <a href={amb.youtube} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#EC4899] flex items-center gap-1.5"><YoutubeLogo size={14} weight="bold" /> YouTube</a>}
              {amb.linkedin && <a href={amb.linkedin} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#22D3EE] flex items-center gap-1.5"><LinkedinLogo size={14} weight="bold" /> LinkedIn</a>}
            </div>
          </div>
        </div>
        {amb.bio && <div className="text-sm text-muted mt-4 leading-relaxed">{amb.bio}</div>}
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {skills.map(s => <span key={s} className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">{s}</span>)}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><InstagramLogo size={12} weight="bold" /> Followers</div><div className="text-white text-lg font-bold mt-1">{(amb.instagram_followers || 0).toLocaleString()}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><ChartLineUp size={12} weight="bold" /> Performance</div><div className="text-white text-lg font-bold mt-1">{amb.performance_score || 0}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><Stack size={12} weight="bold" /> Projects</div><div className="text-white text-lg font-bold mt-1">{apps.filter(a => a.status === "Approved").length}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><CheckCircle size={12} weight="bold" /> Earned</div><div className="text-white text-lg font-bold mt-1">{fmtMoney(totalEarned)}</div></div>
      </div>

      {/* Applications */}
      <SectionTitle title={`Applications (${apps.length})`} subtitle="Projects this creator has applied to" />
      {apps.length === 0 ? <EmptyState icon={ClipboardText} title="No applications yet" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {apps.map(a => (
            <div key={a.id} className="glass p-4" data-testid={`amb-app-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Link to={`/admin/projects/${a.project_id}`} className="text-white font-bold hover:text-[#22D3EE] truncate" data-testid={`amb-app-project-${a.id}`}>{a.project_title}</Link>
                <StatusPill status={a.status} />
              </div>
              <div className="text-[11px] text-muted mt-1">Applied {fmtDate(a.applied_at || a.created_at)}</div>
              {a.status === "Pending" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => actApp(a.id, "approve")} className="btn-ghost flex-1 text-[#A3E635] flex items-center justify-center gap-1.5 text-sm" data-testid={`amb-approve-app-${a.id}`}><CheckCircle size={16} weight="bold" /> Approve</button>
                  <button onClick={() => actApp(a.id, "reject")} className="btn-ghost flex-1 text-[#EC4899] flex items-center justify-center gap-1.5 text-sm" data-testid={`amb-reject-app-${a.id}`}><XCircle size={16} weight="bold" /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Submissions */}
      <SectionTitle title={`Submissions (${subs.length})`} subtitle="Task proofs by this creator" />
      {subs.length === 0 ? <EmptyState title="No submissions" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {subs.map(s => (
            <div key={s.id} className="glass p-4" data-testid={`amb-sub-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-bold truncate">{s.task_title}</div>
                  <Link to={`/admin/projects/${s.project_id}`} className="text-xs text-muted hover:text-[#22D3EE] truncate block">{s.project_title}</Link>
                </div>
                <StatusPill status={s.status} />
              </div>
              {s.feedback && <div className="text-xs text-[#F59E0B] mt-2">Feedback: {s.feedback}</div>}
            </div>
          ))}
        </div>
      )}
      {AuditModal}
      {InsightsModal}
    </div>
  );
}
